import { useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { Alert, Modal } from '@pmi/dsm-react';
import IconDanger from '@pmi/dsm-react/dist/components/icons/16/CircleRemoveIcon';
import { accountSelector } from '../../../../../shared/store/shared/accountSlice';
import usePermissions from '../../../../../shared/hooks/usePermissions';
import { useGetDealsListQuery } from '../../../../../shared/store/shared/dealsApiSlice';
import { reportError } from '../../../../../shared/analytics';
import PERMISSIONS from '../../../../../shared/permissions';
import styles from './editModal.module.scss';
import wizardStyles from '../../../../../shared/components/InviteWizard/wizard.module.scss';
import { User } from '../../../../../shared/models/UserModels';
import SkeletonLoader from '../../../../../shared/components/SkeletonLoader';
import EditModalContent, { ProductListData } from './EditModalContent';

interface EditModalProps {
  user: User;
  onCancel: () => void;
  editMode?: 'full' | 'product' | 'restore';
}

export default function EditModal({
  user,
  onCancel = () => {},
  editMode = 'full',
}: EditModalProps) {
  const { currentAccountId } = useSelector(accountSelector);
  const { hasAccess, isLoading: permissionLoading } = usePermissions();
  const {
    data: deals,
    isLoading,
    isFetching,
    isError,
  } = useGetDealsListQuery(currentAccountId, { skip: permissionLoading });
  const [products, setProducts] = useState<ProductListData[]>([]);
  const errorMessage = 'Failed to load agreements';
  useEffect(() => {
    if (isError) reportError(errorMessage);
  }, [isError]);
  const accessibleDeals = useMemo(() => {
    const hasPermission = hasAccess({
      permissions: [PERMISSIONS.ManageDealUsers],
    });
    return hasPermission ? deals ?? [] : [];
  }, [isLoading, isFetching, permissionLoading, deals]);

  const [animationClassName, setAnimationClassName] = useState('hidden');

  useEffect(() => {
    setTimeout(() => setAnimationClassName(''), 0);
  }, []);

  useEffect(() => {
    // create a product list with the agreements that has access to the product
    // Array { product: ProductApiModel, agreements: DealApiModel[] }
    let temp = [...products];
    accessibleDeals.map((d) => {
      d.products.map((p) => {
        const f = temp.find(
          (x) => x.product.sku.toLowerCase() === p.sku.toLowerCase(),
        );
        if (!f || f === undefined) {
          temp.push({ product: p, agreements: [d] });
        } else {
          temp = temp.map((t) => {
            if (t.product.sku.toLowerCase() === p.sku.toLowerCase()) {
              t.agreements.push(d);
            }
            return t;
          });
        }
        return p;
      });
      return d;
    });
    setProducts(temp);
  }, [accessibleDeals]);

  return (
    <Modal
      show
      customClassName={`m-0 ${styles.editModal} ${animationClassName}`}
      variant="input"
      onClose={onCancel}
      buttons={[]}
    >
      <div className={wizardStyles.wizardContent}>
        <div className="wizard-container">
          <div className="wizard-container-inner">
            {(isLoading || isFetching) && <SkeletonLoader />}
            {isError && (
              <Alert
                variant="danger"
                alertText={errorMessage}
                icon={IconDanger}
              />
            )}
            {!isLoading && !isFetching && !isError && (
              <EditModalContent
                agreements={accessibleDeals}
                user={user}
                onCancel={onCancel}
                mode={editMode}
              />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
